import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'wouter';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { usePasswordReset } from '@/hooks/use-profile';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { passwordResetRequestSchema, type PasswordResetRequestFormData } from '@/schemas/profile.schema';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { LanguagePill } from '@/components/chrome/LanguagePill';

export default function ForgotPasswordPage() {
  const { t } = useTranslation('auth');
  const [, setLocation] = useLocation();
  const { requestPasswordReset, isRequesting, requestSuccess } = usePasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<PasswordResetRequestFormData>({
    resolver: zodResolver(passwordResetRequestSchema)
  });

  const onSubmit = async (data: PasswordResetRequestFormData) => {
    await requestPasswordReset(data as { email: string });
  };

  return (
    <div className="container max-w-md mx-auto py-12 px-4">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex justify-end mb-4">
        <LanguagePill />
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            {t('forgotPassword.title')}
          </CardTitle>
          <CardDescription>
            {requestSuccess
              ? t('forgotPassword.descriptionSuccess')
              : t('forgotPassword.description')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!requestSuccess ? (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <Alert>
                <AlertDescription className="text-sm">
                  {t('forgotPassword.hint')}
                </AlertDescription>
              </Alert>

              <FormField label="Email Address" error={errors.email?.message}>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('forgotPassword.emailPlaceholder')}
                  {...register('email')}
                  autoFocus
                />
              </FormField>

              <Button type="submit" className="w-full" disabled={isRequesting}>
                {isRequesting ? t('forgotPassword.sending') : t('forgotPassword.button')}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => setLocation('/login')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('forgotPassword.backToLogin')}
              </Button>
            </form>
          ) : (
            <div className="text-center space-y-6 py-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-green-100 p-3">
                  <CheckCircle className="w-12 h-12 text-green-600" />
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-xl font-bold">{t('forgotPassword.emailSent')}</h3>
                <p className="text-muted-foreground text-sm">
                  {t('forgotPassword.emailSentMessage')}
                </p>
                <p className="text-muted-foreground text-sm">
                  {t('forgotPassword.checkSpam')}
                </p>
              </div>

              <Button
                onClick={() => setLocation('/login')}
                className="w-full"
              >
                {t('forgotPassword.returnToLogin')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
